// Hook para gerenciar locks de recursos
import { useEffect, useRef, useState, useCallback } from 'react';
import { apiService } from '@/services/api';
import { useAuth } from '@/contexts/auth-hook';
import { useWebSocket, LockEvent } from './useWebSocket';

export type TipoRecurso = 'apreciacao' | 'maquina' | 'ativo';

export interface LockInfo {
  bloqueado: boolean;
  lock?: {
    id: string;
    tipoRecurso: TipoRecurso;
    recursoId: string;
    usuarioId: string;
    usuarioNome?: string;
    expiresAt: string;
  };
  mensagem?: string;
}

export function useLock(
  tipoRecurso: TipoRecurso,
  recursoId: string | null | undefined,
  opcoes?: {
    autoLock?: boolean; // Criar lock automaticamente quando o recurso for carregado
    autoRenovar?: boolean; // Renovar lock automaticamente antes de expirar
    intervaloRenovacao?: number; // Intervalo em ms para renovar (padrão: 30 segundos para heartbeat)
    onBloqueado?: (lock: LockInfo) => void; // Callback quando detectar bloqueio
    enableHeartbeat?: boolean; // Enviar heartbeat durante edição
  }
) {
  const { user } = useAuth();
  const { joinResource, leaveResource, sendHeartbeat, onLockEvent, connected } = useWebSocket();
  const [lockInfo, setLockInfo] = useState<LockInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervaloRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const joinedRoomRef = useRef<string | null>(null); // Rastrear qual sala estamos
  const verificandoRef = useRef(false); // Evitar múltiplas verificações simultâneas
  const usuarioId = user?.id || '';

  // Verificar lock
  const verificar = useCallback(async (): Promise<LockInfo | null> => {
    if (!recursoId) return null;

    // Evitar múltiplas verificações simultâneas
    if (verificandoRef.current) {
      return null;
    }

    verificandoRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.verificarLock(tipoRecurso, recursoId);
      if (response.success && response.data) {
        const info: LockInfo = {
          bloqueado: response.data.bloqueado || false,
          lock: response.data.lock,
          mensagem: response.data.mensagem,
        };

        setLockInfo(info);

        // Se está bloqueado por outro usuário, chamar callback
        if (info.bloqueado && info.lock && info.lock.usuarioId !== usuarioId) {
          opcoes?.onBloqueado?.(info);
        }

        return info;
      }
      return null;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erro ao verificar lock';
      setError(errorMsg);
      return null;
    } finally {
      setLoading(false);
      verificandoRef.current = false;
    }
  }, [tipoRecurso, recursoId, usuarioId, opcoes]);

  // Criar lock
  const criar = useCallback(
    async (_usuarioNome?: string, duracaoMinutos?: number): Promise<boolean> => {
      if (!recursoId || !usuarioId) return false;

      setLoading(true);
      setError(null);

      try {
        const response = await apiService.criarLock({
          tipoRecurso,
          recursoId,
          duracaoMinutos,
        });

        if (response.success) {
          // Atualizar lockInfo diretamente sem chamar verificar novamente
          if (response.data) {
            setLockInfo({
              bloqueado: true,
              lock: response.data.lock,
              mensagem: response.data.mensagem,
            });
          }
          return true;
        } else {
          setError(response.message || 'Erro ao criar lock');
          if (response.errors && response.errors.length > 0) {
            setError(response.errors.join(', '));
          }
          return false;
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Erro ao criar lock';
        setError(errorMsg);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [tipoRecurso, recursoId, usuarioId]
  );

  // Remover lock
  const remover = useCallback(async (): Promise<boolean> => {
    if (!recursoId) return false;

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.removerLock(tipoRecurso, recursoId);

      if (response.success) {
        setLockInfo(null);
        return true;
      } else {
        // Se o lock não existe ou não é nosso, não é um erro crítico
        // (pode ter expirado ou já foi removido)
        if (response.message?.includes('não encontrado') || 
            response.message?.includes('permissão') ||
            response.message?.includes('não tem permissão')) {
          // Limpar estado local mesmo assim
          setLockInfo(null);
          return true; // Considerar sucesso para não bloquear o fluxo
        }
        setError(response.message || 'Erro ao remover lock');
        return false;
      }
    } catch (err) {
      // Em caso de erro de rede ou 403, limpar estado local
      const errorMsg = err instanceof Error ? err.message : 'Erro ao remover lock';
      console.warn('Erro ao remover lock (não crítico):', errorMsg);
      setLockInfo(null);
      return true; // Considerar sucesso para não bloquear o fluxo
    } finally {
      setLoading(false);
    }
  }, [tipoRecurso, recursoId, usuarioId]);

  // Renovar lock
  const renovar = useCallback(
    async (duracaoMinutos?: number): Promise<boolean> => {
      if (!recursoId || !usuarioId) return false;

      try {
        const response = await apiService.renovarLock(
          tipoRecurso,
          recursoId,
          duracaoMinutos
        );

        if (response.success && response.data) {
          // Atualizar lockInfo diretamente sem chamar verificar novamente
          setLockInfo({
            bloqueado: true,
            lock: response.data.lock,
            mensagem: response.data.mensagem,
          });
          return true;
        }
        return false;
      } catch (err) {
        console.error('Erro ao renovar lock:', err);
        return false;
      }
    },
    [tipoRecurso, recursoId, usuarioId]
  );

  // Verificar se o lock é do usuário atual
  const eMeuLock = useCallback((): boolean => {
    if (!lockInfo?.bloqueado || !lockInfo?.lock?.usuarioId || !usuarioId) {
      return false;
    }
    
    // Comparar como strings para garantir compatibilidade
    const lockUserId = String(lockInfo.lock.usuarioId);
    const currentUserId = String(usuarioId);
    const isMyLock = lockUserId === currentUserId;
    
    // Debug apenas em desenvolvimento
    if (import.meta.env.DEV && lockInfo?.bloqueado) {
      console.log('[Lock Debug]', {
        lockUserId,
        currentUserId,
        isMyLock,
        bloqueado: lockInfo.bloqueado
      });
    }
    
    return isMyLock;
  }, [lockInfo, usuarioId]);

  // Verificar se está bloqueado por outro usuário
  const bloqueadoPorOutro = useCallback((): boolean => {
    if (!lockInfo?.bloqueado || !lockInfo?.lock?.usuarioId || !usuarioId) {
      return false;
    }
    
    // Comparar como strings para garantir compatibilidade
    const lockUserId = String(lockInfo.lock.usuarioId);
    const currentUserId = String(usuarioId);
    const isBlockedByOther = lockUserId !== currentUserId;
    
    return isBlockedByOther;
  }, [lockInfo, usuarioId]);

  // Efeito para escutar eventos WebSocket
  useEffect(() => {
    if (!recursoId || !connected) {
      // Se desconectou ou não tem recursoId, sair da sala anterior se existir
      if (joinedRoomRef.current) {
        const [prevTipo, prevId] = joinedRoomRef.current.split(':');
        leaveResource(prevTipo, prevId);
        joinedRoomRef.current = null;
      }
      return;
    }

    const roomKey = `${tipoRecurso}:${recursoId}`;
    
    // Só entrar se não estiver na sala ainda
    if (joinedRoomRef.current !== roomKey) {
      // Sair da sala anterior se existir
      if (joinedRoomRef.current) {
        const [prevTipo, prevId] = joinedRoomRef.current.split(':');
        leaveResource(prevTipo, prevId);
      }
      
      // Entrar na nova sala
      joinResource(tipoRecurso, recursoId);
      joinedRoomRef.current = roomKey;
    }

    // Escutar eventos de lock
    const unsubscribe = onLockEvent((event: LockEvent) => {
      if (event.recursoId === recursoId && event.tipoRecurso === tipoRecurso) {
        if (event.action === 'locked' || event.action === 'updated') {
          setLockInfo({
            bloqueado: true,
            lock: {
              id: '',
              tipoRecurso: event.tipoRecurso,
              recursoId: event.recursoId,
              usuarioId: event.usuarioId,
              usuarioNome: event.usuarioNome,
              expiresAt: event.expiresAt || new Date(Date.now() + 10 * 60 * 1000).toISOString()
            }
          });

          // Se não é nosso lock, chamar callback
          if (event.usuarioId !== usuarioId && opcoes?.onBloqueado) {
            opcoes.onBloqueado({
              bloqueado: true,
              lock: {
                id: '',
                tipoRecurso: event.tipoRecurso,
                recursoId: event.recursoId,
                usuarioId: event.usuarioId,
                usuarioNome: event.usuarioNome,
                expiresAt: event.expiresAt || new Date().toISOString()
              }
            });
          }
        } else if (event.action === 'unlocked') {
          // Se o lock foi removido e não é nosso, limpar
          if (event.usuarioId !== usuarioId) {
            setLockInfo({ bloqueado: false });
          }
        }
      }
    });

    return () => {
      unsubscribe();
      // Só sair se ainda estiver na mesma sala
      if (joinedRoomRef.current === roomKey) {
        leaveResource(tipoRecurso, recursoId);
        joinedRoomRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recursoId, tipoRecurso, connected, usuarioId]);

  // Efeito para verificar lock quando recursoId mudar (apenas se autoLock estiver ativado)
  // Se autoLock estiver desativado, a verificação deve ser manual
  const verificadoRef = useRef<string | null>(null);
  useEffect(() => {
    // Se autoLock está desativado, não fazer verificação automática
    if (!opcoes?.autoLock) {
      return;
    }

    if (!recursoId || !usuarioId) {
      setLockInfo(null);
      verificadoRef.current = null;
      return;
    }

    // Só verificar se ainda não foi verificado para este recursoId
    if (verificadoRef.current === recursoId) {
      return;
    }

    let mounted = true;
    verificadoRef.current = recursoId;

    const verificarECriar = async () => {
      const info = await verificar();
      
      if (!mounted) return;

      // Se autoLock está ativado e não está bloqueado por outro, criar lock automaticamente
      if (opcoes?.autoLock && info && !info.bloqueado) {
        await criar();
      }
    };

    verificarECriar();

    return () => {
      mounted = false;
      if (verificadoRef.current === recursoId) {
        verificadoRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recursoId, opcoes?.autoLock, usuarioId]);

  // Efeito para renovação automática e heartbeat
  useEffect(() => {
    if (!opcoes?.autoRenovar || !recursoId || !eMeuLock() || !connected) {
      if (intervaloRef.current) {
        clearInterval(intervaloRef.current);
        intervaloRef.current = null;
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      return;
    }

    // Renovação do lock (a cada 5 minutos)
    const intervalo = opcoes.intervaloRenovacao || 5 * 60 * 1000;
    intervaloRef.current = setInterval(() => {
      renovar();
    }, intervalo);

    // Heartbeat durante edição (a cada 30 segundos)
    if (opcoes?.enableHeartbeat) {
      heartbeatIntervalRef.current = setInterval(() => {
        sendHeartbeat(tipoRecurso, recursoId);
        // Renovar lock também no heartbeat (resetar timer)
        renovar();
      }, 30000); // 30 segundos
    }

    return () => {
      if (intervaloRef.current) {
        clearInterval(intervaloRef.current);
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [opcoes?.autoRenovar, opcoes?.enableHeartbeat, recursoId, tipoRecurso, eMeuLock, renovar, sendHeartbeat, connected, opcoes?.intervaloRenovacao]);

  // Limpar lock ao desmontar componente
  useEffect(() => {
    return () => {
      if (intervaloRef.current) {
        clearInterval(intervaloRef.current);
      }
      // Remover lock ao sair da página (opcional, pode ser controlado)
      // remover();
    };
  }, [remover]);

  return {
    lockInfo,
    loading,
    error,
    verificar,
    criar,
    remover,
    renovar,
    eMeuLock,
    bloqueadoPorOutro,
    usuarioId,
  };
}

